<template>
  <div class="container-fluid">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h3 class="mb-0">Transfer-Historie</h3>
      <div class="d-flex gap-2 align-items-center">
        <!-- Filter Select -->
        <select 
          v-model="activeFilter" 
          class="form-select form-select-sm quitly-filter-auto-width"
        >
          <option v-for="filter in filters" :key="filter.key" :value="filter.key">
            {{ filter.label }}
          </option>
        </select>
        
        <!-- Admin Toggle (nur für Bereich-Admins) -->
        <button
          v-if="isTeamAdmin"
          @click="viewMode = viewMode === 'user' ? 'admin' : 'user'"
          class="btn btn-sm btn-outline-secondary"
        >
          {{ viewMode === 'user' ? 'Admin-Ansicht' : 'Meine Transfers' }}
        </button>
        
        <!-- Refresh Button -->
        <button
          @click="loadHistory"
          :disabled="loading"
          class="btn btn-sm btn-outline-secondary"
        >
          <font-awesome-icon icon="sync-alt" />
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Laden...</span>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else-if="filteredTransfers.length === 0" class="text-center py-5">
      <div class="text-muted">
        <div class="h1 mb-3">
          <font-awesome-icon icon="clipboard-list" class="text-secondary" />
        </div>
        <div class="h5 mb-2">Keine Transfers gefunden</div>
        <div class="small">
          {{ activeFilter === 'all' ? 'Noch keine Transfers durchgeführt' : 'Keine Transfers in dieser Kategorie' }}
        </div>
      </div>
    </div>

    <!-- Transfer List -->
    <div v-else class="row g-3">
      <div class="col-12" v-for="transfer in filteredTransfers" :key="transfer.id">
        <div class="card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-start">
              <!-- Transfer Info -->
              <div class="d-flex align-items-center">
                <!-- Transfer Type Icon -->
                <div class="me-3">
                  <div v-if="transfer.type === 'purchase'" 
                       class="quitly-icon-circle quitly-icon-circle-warning">
                    <font-awesome-icon icon="shopping-cart" class="fs-5" />
                  </div>
                  <div v-else-if="transfer.transferType === 'personal_to_team'" 
                       class="quitly-icon-circle quitly-icon-circle-primary">
                    <font-awesome-icon icon="arrow-up" class="fs-5" />
                  </div>
                  <div v-else 
                       class="quitly-icon-circle quitly-icon-circle-success">
                    <font-awesome-icon icon="arrow-down" class="fs-5" />
                  </div>
                </div>

                <!-- Transfer Details -->
                <div>
                  <div class="fw-bold">
                    <span v-if="transfer.type === 'purchase'">
                      Bereich-Upgrade Purchase
                      <!-- Admin-Ansicht: Zeige den Namen prominent -->
                      <span v-if="viewMode === 'admin' && transfer.user" class="badge bg-info ms-2">
                        {{ transfer.user.firstName }} {{ transfer.user.lastName }}
                      </span>
                    </span>
                    <span v-else>
                      {{ transfer.transferType === 'personal_to_team' ? 'Personal → Bereich' : 'Bereich → Personal' }}
                      <!-- Admin-Ansicht: Zeige den Namen prominent -->
                      <span v-if="viewMode === 'admin' && transfer.user" class="badge bg-info ms-2">
                        {{ transfer.user.firstName }} {{ transfer.user.lastName }}
                      </span>
                    </span>
                  </div>
                  <div class="text-muted small">
                    {{ formatDate(transfer.transferredAt) }}
                    <!-- Beschreibung für System-Purchases -->
                    <div v-if="transfer.type === 'purchase' && transfer.description" class="mt-1">
                      {{ transfer.description }}
                    </div>
                    <!-- Zusätzliche Admin-Details -->
                    <span v-if="viewMode === 'admin' && transfer.user" class="d-block mt-1">
                      <font-awesome-icon icon="user" class="me-1" />
                      E-Mail: {{ transfer.user.email }}            
                    </span>
                  </div>
                </div>
              </div>

              <!-- Amount and Status -->
              <div class="text-end">
                <div class="fw-bold h5 mb-1">
                  <span :class="[
                    transfer.type === 'purchase' ? 'text-warning' :
                    transfer.transferType === 'personal_to_team' ? 'text-primary' : 'text-success'
                  ]">
                    {{ transfer.amount }} {{ transfer.amount === 1 ? 'Quittung' : 'Quittungen' }}
                  </span>
                </div>
                <div class="small">
                  <span v-if="transfer.transferType === 'personal_to_team' && transfer.remainingAmount !== null" class="text-muted me-2">
                    Verbleibend: {{ transfer.remainingAmount }}
                  </span>
                  <span :class="[
                    'badge',
                    transfer.status === 'completed' ? 'bg-success' :
                    transfer.status === 'active' ? 'bg-success' :
                    'bg-secondary'
                  ]">
                    {{ transfer.status === 'completed' ? 'Abgeschlossen' :
                       transfer.status === 'active' ? 'Aktiv' : 'Verbraucht' }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Consumption Details (nur bei Admin-Ansicht und wenn Verbrauch vorhanden) -->
            <div v-if="viewMode === 'admin' && transfer.consumptions && transfer.consumptions.length > 0" 
                 class="mt-3 pt-3 border-top">
              <div class="text-muted small mb-2">Verbrauch:</div>
              <div class="row g-2">
                <div class="col-12" v-for="consumption in transfer.consumptions" :key="consumption.id">
                  <div class="d-flex justify-content-between align-items-center small bg-light p-2 rounded">
                    <div>
                      <span class="fw-bold">{{ consumption.consumedAmount }} {{ consumption.consumedAmount === 1 ? 'Quittung' : 'Quittungen' }}</span>
                      <span class="text-muted ms-2">
                        {{ consumption.consumptionType === 'receipt_generation' ? 'Quittung' : consumption.consumptionType }}
                      </span>
                      <span v-if="consumption.receipt" class="text-muted ms-1">
                        {{ consumption.receipt.title || `#${consumption.receipt.id.slice(0, 8)}` }}
                      </span>
                    </div>
                    <div class="text-muted">
                      {{ formatDate(consumption.consumedAt) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <nav v-if="pagination && pagination.totalPages > 1" class="mt-4">
      <div class="d-flex justify-content-center align-items-center gap-3">
        <button
          @click="previousPage"
          :disabled="pagination.page <= 1 || loading"
          class="btn btn-outline-secondary btn-sm"
        >
          ← Vorherige
        </button>
        
        <span class="text-muted small">
          Seite {{ pagination.page }} von {{ pagination.totalPages }}
        </span>
        
        <button
          @click="nextPage"
          :disabled="pagination.page >= pagination.totalPages || loading"
          class="btn btn-outline-secondary btn-sm"
        >
          Nächste →
        </button>
      </div>
    </nav>
  </div>
</template>

<script>
import { coinTransferApi } from '../../api/coinTransfer'

export default {
  name: 'CoinTransferHistory',
  props: {
    teamId: {
      type: String,
      required: true
    },
    isTeamAdmin: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      transfers: [],
      loading: false,
      viewMode: 'user', 
      activeFilter: 'all',
      pagination: null,
      filters: [
        { key: 'all', label: 'Alle' },
        { key: 'purchase', label: 'Bereich-Upgrades' },
        { key: 'personal_to_team', label: 'Personal → Bereich' },
        { key: 'team_to_personal', label: 'Bereich → Personal' },
        { key: 'active', label: 'Aktiv' },
        { key: 'consumed', label: 'Verbraucht' }
      ]
    }
  },
  computed: {
    filteredTransfers() {
      if (this.activeFilter === 'all') {
        return this.transfers
      }
      
      if (this.activeFilter === 'purchase') {
        return this.transfers.filter(t => t.type === 'purchase')
      }
      
      if (this.activeFilter === 'active') {
        return this.transfers.filter(t => t.status === 'active' || t.status === 'completed')
      }
      
      if (this.activeFilter === 'consumed') {
        return this.transfers.filter(t => t.status === 'fully_consumed')
      }
      
      return this.transfers.filter(t => t.transferType === this.activeFilter)
    }
  },
  watch: {
    viewMode() {
      this.loadHistory()
    },
    teamId() {
      this.loadHistory()
    }
  },
  mounted() {
    this.loadHistory()
  },
  methods: {
    async loadHistory(page = 1) {
      this.loading = true
      
      try {
        let response
        if (this.viewMode === 'admin' && this.isTeamAdmin) {
          response = await coinTransferApi.getTeamTransferHistory(this.teamId, page)
        } else {
          response = await coinTransferApi.getUserTransferHistory(this.teamId, page)
        }
        
        this.transfers = response.transfers
        this.pagination = response.pagination
      } catch (error) {
        console.error('Fehler beim Laden der Transfer-Historie:', error)
        this.transfers = []
        this.pagination = null
      } finally {
        this.loading = false
      }
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleString('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    previousPage() {
      this.loadHistory(this.pagination.page - 1)
    },

    nextPage() {
      this.loadHistory(this.pagination.page + 1)
    }
  }
}
</script>
